/**
 * Trajectoires pluriannuelles 2026-2030.
 *
 * Les scénarios 1 et 2 sont repris du classeur voté sans recalcul : ce sont
 * des hypothèses de gestion. Le scénario 3 est construit à partir des données
 * réelles — cible d'effectifs, pente du produit moyen, extinction de la
 * convention Audiens après 2027.
 */
import { AUDIENS, EXERCICES_TRAJECTOIRE, PLAN_VOTE, SCENARIO_3, SCENARIOS_VOTES, SEUIL_SECURITE_VOTE, } from './configuration.js';
import { sommeExacte, effectifsInterpoles } from './adherents.js';
import { produitsMoyensProjetes } from './regression.js';
/** Seuil de sécurité **voté** d'un exercice ; extrapolé si hors série connue. */
export function seuilVote(annee) {
    const s = SEUIL_SECURITE_VOTE[annee];
    if (s !== undefined)
        return s;
    const connues = Object.keys(SEUIL_SECURITE_VOTE).map(Number).sort((a, b) => a - b);
    const derniere = connues[connues.length - 1];
    const avantDerniere = connues[connues.length - 2];
    if (derniere === undefined || avantDerniere === undefined)
        return 0;
    const vDer = SEUIL_SECURITE_VOTE[derniere] ?? 0;
    const vAv = SEUIL_SECURITE_VOTE[avantDerniere] ?? 0;
    const pas = (vDer - vAv) / (derniere - avantDerniere);
    return vDer + pas * (annee - derniere);
}
/** Seuil applicable : celui calculé s'il existe, le voté sinon. */
export function seuil(annee, seuils) {
    return seuils?.[annee] ?? seuilVote(annee);
}
/**
 * Assemble une trajectoire à partir de ses trois séries et de la réserve de
 * départ, en chaînant les résultats sur les fonds propres.
 */
export function construireTrajectoire(params) {
    const { cle, libelle, annees, adherents, produits, charges, reserveDepart } = params;
    if (adherents.length !== annees.length || produits.length !== annees.length || charges.length !== annees.length) {
        throw new RangeError(`séries de longueurs incohérentes pour la trajectoire « ${cle} »`);
    }
    let reserve = reserveDepart;
    const lignes = [];
    for (let i = 0; i < annees.length; i++) {
        const annee = annees[i];
        const p = produits[i];
        const c = charges[i];
        const resultat = sommeExacte([p, -c]);
        reserve = sommeExacte([reserve, resultat]);
        lignes.push({
            annee,
            adherents: adherents[i],
            produits: p,
            charges: c,
            resultat,
            reserve,
            seuil: seuil(annee, params.seuils),
        });
    }
    const resultatCumule = sommeExacte(lignes.map((l) => l.resultat));
    const franchissement = lignes.find((l) => l.reserve < l.seuil);
    const equilibre = lignes.find((l) => l.resultat >= 0);
    const creux = lignes.reduce((min, l) => (l.reserve < min.reserve ? l : min), lignes[0]);
    return {
        cle,
        libelle,
        annees: lignes,
        resultatCumule,
        reserveFinale: lignes[lignes.length - 1].reserve,
        nbExercicesDeficitaires: lignes.filter((l) => l.resultat < 0).length,
        franchissementSeuil: franchissement?.annee ?? null,
        retourEquilibre: equilibre?.annee ?? null,
        creuxReserve: { annee: creux.annee, montant: creux.reserve },
    };
}
/**
 * Séries votées ramenées à la fenêtre demandée.
 *
 * Les tableaux du classeur sont indexés depuis le premier exercice du plan. En
 * 2027, la trajectoire commence à 2027 : il faut donc en retirer les premiers
 * termes, non les relire depuis le début. Un exercice hors du plan reçoit la
 * dernière valeur connue — c'est explicite et l'application signale que rien
 * ne le couvre.
 */
function tranche(serie, exercices) {
    return exercices.map((annee) => {
        const i = annee - PLAN_VOTE.premier;
        if (i < 0)
            return serie[0] ?? 0;
        return serie[i] ?? serie[serie.length - 1] ?? 0;
    });
}
/** Scénarios 1 et 2, repris du classeur voté. */
export function scenariosVotes(reserveDepart, seuils, exercices = EXERCICES_TRAJECTOIRE) {
    return ['sc1', 'sc2'].map((cle) => {
        const s = SCENARIOS_VOTES[cle];
        return construireTrajectoire({
            cle,
            libelle: s.libelle,
            annees: exercices,
            adherents: tranche(s.adherents, exercices),
            produits: tranche(s.produits, exercices),
            charges: tranche(s.charges, exercices),
            reserveDepart,
            ...(seuils ? { seuils } : {}),
        });
    });
}
/**
 * Scénario 3 : cible de 300 adhérents payeurs en 2030, produit moyen
 * prolongeant la pente observée hors exercices atypiques, extinction de la
 * convention Audiens après 2027.
 */
export function scenario3(e) {
    const annees = e.exercices ?? EXERCICES_TRAJECTOIRE;
    const nbPas = annees.length - 1;
    const adherents = effectifsInterpoles(e.payeursExerciceCourant, SCENARIO_3.cible2030, nbPas);
    const produitsMoyens = produitsMoyensProjetes(e.produitMoyenExerciceCourant, e.pente, nbPas);
    const socleConventionnel = Object.values(SCENARIO_3.ressourcesConventionnelles).reduce((s, v) => s + v, 0);
    const produits = [e.produitsExerciceCourant];
    const charges = [e.chargesExerciceCourant];
    for (let i = 1; i <= nbPas; i++) {
        const annee = annees[i];
        const audiens = annee <= AUDIENS.derniereAnnee ? AUDIENS.montantAnnuel : 0;
        const cotisations = adherents[i] * produitsMoyens[i];
        produits.push(sommeExacte([cotisations, socleConventionnel, audiens]));
        charges.push(Math.round(e.chargesExerciceCourant * (1 + SCENARIO_3.progressionCharges) ** i * 100) / 100);
    }
    return construireTrajectoire({
        cle: 'sc3',
        libelle: SCENARIO_3.libelle,
        annees,
        adherents,
        produits,
        charges,
        reserveDepart: e.reserveDepart,
        ...(e.seuils ? { seuils: e.seuils } : {}),
    });
}
/** Produits moyens de chaque scénario, pour le graphique de l'onglet adhérents. */
export function produitsMoyensScenarios(sc3) {
    return {
        sc1: [...SCENARIOS_VOTES.sc1.produitMoyen],
        sc2: [...SCENARIOS_VOTES.sc2.produitMoyen],
        sc3: produitsMoyensProjetes(sc3.produitMoyenExerciceCourant, sc3.pente, (sc3.exercices ?? EXERCICES_TRAJECTOIRE).length - 1),
    };
}
//# sourceMappingURL=scenarios.js.map